import React, { createContext, useCallback, useMemo, useEffect } from 'react';

import { generateDetailedItem, cartExpiryKey, cartKey, isCartItem } from './util';
import type { CartItem, CartItemsState, NewCartItem, DetailedCartItem } from './models';
import { useSafeContext, createNamedContext } from '@clover-util/react-context';
import { useLocalStorageKeyedCollection } from 'hooks/useKeyedCollection';
import { LocalStorage } from 'util/Storage';
import { useMerchant } from '../merchant';
import { useStorageExpiry } from '../../useStorageExpiry';

export * from './models';

type CartDispatchState = Pick<CartItemsState, 'add' | 'clear' | 'remove' | 'update'>;
const CartDispatchContext = createNamedContext<CartDispatchState>('CartDispatchContext');

export function useCartDispatch() {
  return useSafeContext(useCartDispatch, CartDispatchContext);
}

const CartStateContext = createContext<Omit<CartItemsState, keyof CartDispatchState> | undefined>(undefined);
// eslint-disable-next-line
CartStateContext.displayName = 'CartStateContext';

export const CartProvider: React.FC = ({ children }) => {
  const { merchant } = useMerchant();

  const merchantId = merchant?.merchantUuid;

  const [items, collection] = useLocalStorageKeyedCollection<DetailedCartItem>((merchantId && cartKey(merchantId)) || undefined, () => {
    // OLD Cart Support
    const oldItems = (merchantId && LocalStorage.tryGetJSONItem<DetailedCartItem[]>(`cart_${merchantId}`)) || [];

    return (Array.isArray(oldItems) ? oldItems : Object.values<DetailedCartItem>(oldItems)).map(value => ({ key: value.cartId, value }));
  });

  const [isExpired, updateExpiry] = useStorageExpiry(cartExpiryKey);

  useEffect(() => {
    if (isExpired) collection.clear();
  }, [isExpired, collection]);

  // Clear old local storage keys
  useEffect(() => {
    if (!merchantId) return;
    LocalStorage.tryRemoveItem(`cart_${merchantId}`);
    LocalStorage.tryRemoveItem(`cartExpiry_${merchantId}`);
  }, [merchantId]);

  const { itemCount, subtotal } = useMemo(() => items.reduce(({ itemCount: cartItemCount, subtotal: cartItemSubtotal }, { value: item }) => ({
    itemCount: cartItemCount + item.quantity,
    subtotal: cartItemSubtotal + item.total,
  }), { itemCount: 0, subtotal: 0 }), [items]);

  const dispatch = useMemo(() => ({
    add: (...newItems: NewCartItem[]) => newItems.forEach(newItem => {
      if (isCartItem(newItem)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('cart id found in add function. please use update instead');
        }

        return;
      }

      const detailed = generateDetailedItem(newItem);

      if (detailed.quantity === 0) return;

      collection.put(detailed.cartId, detailed);
      updateExpiry();
    }),
    update: (cartItem: CartItem) => {
      const detailed = generateDetailedItem(cartItem);
      if (detailed.quantity > 0) {
        collection.put(detailed.cartId, detailed);
      } else {
        collection.remove(detailed.cartId);
      }
      updateExpiry();
    },
    remove: (cartItem: CartItem | string) => collection.remove(typeof cartItem === 'string' ? cartItem : cartItem.cartId),
    clear: collection.clear,
  }), [collection, updateExpiry]);

  return (
    <CartDispatchContext.Provider value={dispatch}>
      <CartStateContext.Provider
        value={{
          find: useCallback(cartId => collection.get(cartId), [collection]),
          items: useMemo(() => items.map(({ key, value }) => ({ ...value, cartId: key })), [items]),
          subtotal,
          itemCount,
          isValid: items.length > 0 && subtotal > 0
        }}
      >
        {children}
      </CartStateContext.Provider>
    </CartDispatchContext.Provider>
  );
};

export function useCart() {
  return {
    ...useCartDispatch(),
    ...useSafeContext(useCart, CartStateContext),
  };
}
